import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Search, Mail, Phone, Globe } from 'lucide-react';
import api from '../api';

export default function PartnersPage() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    useEffect(() => {
        loadPartners();
    }, []);

    const loadPartners = async () => {
        try {
            const data = await api.getPartners();
            setPartners(data);
        } catch (error) {
            console.error('Error loading partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const categories = [...new Set(partners.map(p => p.category).filter(Boolean))];
    const filteredPartners = partners.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.description?.toLowerCase().includes(search.toLowerCase()) ||
            p.artforms?.some(a => a.toLowerCase().includes(search.toLowerCase()));
        const matchesCategory = !categoryFilter || p.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    return (
        <main className="main">
            {/* Hero Section */}
            <section style={{
                background: 'var(--primary)',
                padding: '80px 24px',
                textAlign: 'center',
                color: 'white'
            }}>
                <div className="container">
                    <h1 style={{
                        fontSize: 'clamp(32px, 5vw, 48px)',
                        fontWeight: '700',
                        marginBottom: '16px'
                    }}>
                        Our Arts Partners
                    </h1>
                    <p style={{
                        fontSize: '18px',
                        opacity: 0.9,
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        Discover talented organizations preserving and promoting traditional Indian arts in Singapore
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="container" style={{ paddingTop: '40px' }}>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    marginBottom: '32px'
                }}>
                    <div className="search-input" style={{ flex: '1 1 300px' }}>
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search partners, art forms..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="form-input"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        style={{ flex: '0 1 200px' }}
                    >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Results Count */}
                <div style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>
                    {filteredPartners.length} partner{filteredPartners.length !== 1 ? 's' : ''} found
                </div>
            </section>

            {/* Partners Grid */}
            <section className="container" style={{ paddingBottom: '80px' }}>
                {loading ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                        gap: '24px'
                    }}>
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="skeleton" style={{ height: '400px', borderRadius: '16px' }} />
                        ))}
                    </div>
                ) : filteredPartners.length > 0 ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                        gap: '24px'
                    }}>
                        {filteredPartners.map((partner) => (
                            <div key={partner.id} className="card fade-in" style={{
                                padding: '0',
                                overflow: 'hidden',
                                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                            }}>
                                {/* Header with gradient */}
                                <div style={{
                                    background: 'var(--primary)',
                                    padding: '24px',
                                    color: 'white'
                                }}>
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '16px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '16px',
                                        fontSize: '28px',
                                        fontWeight: '700'
                                    }}>
                                        {partner.name.charAt(0)}
                                    </div>
                                    <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                                        {partner.name}
                                    </h3>
                                    <span style={{
                                        display: 'inline-block',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }}>
                                        {partner.category}
                                    </span>
                                </div>

                                {/* Content */}
                                <Link to={`/partners/${partner.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                                    <div style={{ padding: '24px' }}>
                                        <p style={{
                                            fontSize: '14px',
                                            color: 'var(--text-muted)',
                                            marginBottom: '20px',
                                            lineHeight: '1.6',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            minHeight: '63px'
                                        }}>
                                            {partner.description}
                                        </p>

                                        {/* Art Forms */}
                                        {/* ... artforms code omitted for brevity in replacement but kept in file ... */}
                                        {/* (Actually I should provide the full block to be safe) */}
                                        {partner.artforms && partner.artforms.length > 0 && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <div style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: 'var(--text-muted)',
                                                    marginBottom: '8px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Art Forms
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {partner.artforms.slice(0, 4).map((art, idx) => (
                                                        <span key={idx} style={{
                                                            fontSize: '12px',
                                                            background: 'var(--bg-secondary)',
                                                            color: 'var(--text)',
                                                            padding: '4px 10px',
                                                            borderRadius: '6px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {art}
                                                        </span>
                                                    ))}
                                                    {partner.artforms.length > 4 && (
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: 'var(--text-muted)',
                                                            padding: '4px 10px'
                                                        }}>
                                                            +{partner.artforms.length - 4}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Services */}
                                        {partner.services && partner.services.length > 0 && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <div style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: 'var(--text-muted)',
                                                    marginBottom: '8px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Services
                                                </div>
                                                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                                                    {partner.services.slice(0, 3).join(' • ')}
                                                    {partner.services.length > 3 && ' • ...'}
                                                </div>
                                            </div>
                                        )}

                                        {/* Divider */}
                                        <div style={{
                                            height: '1px',
                                            background: 'var(--border)',
                                            margin: '20px 0'
                                        }} />

                                        {/* Contact Info */}
                                        <div style={{ fontSize: '13px' }}>
                                            {partner.contact_person && (
                                                <div style={{ marginBottom: '12px' }}>
                                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                        {partner.contact_person}
                                                    </div>
                                                    {partner.role && (
                                                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                                            {partner.role}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {partner.email && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '8px',
                                                    color: 'var(--text-muted)'
                                                }}>
                                                    <Mail size={14} />
                                                    <span style={{ color: 'var(--primary)' }}>
                                                        {partner.email}
                                                    </span>
                                                </div>
                                            )}

                                            {partner.phone && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '8px',
                                                    color: 'var(--text-muted)'
                                                }}>
                                                    <Phone size={14} />
                                                    <span>{partner.phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card" style={{
                        padding: '80px 24px',
                        textAlign: 'center'
                    }}>
                        <Building2 size={64} style={{
                            color: 'var(--text-muted)',
                            margin: '0 auto 24px',
                            opacity: 0.5
                        }} />
                        <h3 style={{ marginBottom: '12px', fontSize: '24px' }}>
                            No partners found
                        </h3>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
                            Try adjusting your search or filters
                        </p>
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setSearch('');
                                setCategoryFilter('');
                            }}
                        >
                            Clear Filters
                        </button>
                    </div>
                )}
            </section>

            {/* CTA Section */}
            <section style={{
                background: 'var(--bg-secondary)',
                padding: '60px 24px',
                textAlign: 'center'
            }}>
                <div className="container">
                    <h2 style={{ fontSize: '32px', marginBottom: '16px' }}>
                        Interested in partnering with ATAM?
                    </h2>
                    <p style={{
                        color: 'var(--text-muted)',
                        marginBottom: '32px',
                        maxWidth: '500px',
                        margin: '0 auto 32px'
                    }}>
                        Join our network of arts organizations and help preserve traditional Indian performing arts
                    </p>
                    <Link to="/contact" className="btn btn-primary btn-lg">
                        Get in Touch
                    </Link>
                </div>
            </section>
        </main>
    );
}
